import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ListChecks,
  Image as ImageIcon,
  Clock,
  Minus,
  Plus,
  X,
} from "lucide-react";
import { useGlobal } from "../../context/GlobalContext";
import storage from "../../assets/js/Global";
import fileToBase64 from "../../utils/fileToBase64";
import {
  formatUtcToLocalDate,
  formatUtcToLocalDateTime,
  getLocalDateFromUtc,
  parseUtc,
} from "../../utils/timezone";
import { images } from "../../assets/images";

const DashboardDetails = () => {
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const job_pid = location.state?.job_pid;

  const {
    getJobDetail,
    jobDetail,
    startJob,
    saveDamageNotes,
    uploadWorkPhoto,
    getJobsList,
  } = useGlobal();

  const [activeTab, setActiveTab] = useState("workChecklist");
  const [checkedItems, setCheckedItems] = useState({});
  const [newImages, setNewImages] = useState([]);
  const [workPhotos, setWorkPhotos] = useState([]);
  const [jobStarted, setJobStarted] = useState(false);
  const [damageNotes, setDamageNotes] = useState("");
  const [assignedJobsList, setAssignedJobsList] = useState([]);

  // ── Start-job ETA + live timeline ──
  const [showEtaModal, setShowEtaModal] = useState(false);
  const [etaHours, setEtaHours] = useState(0);
  const [etaMins, setEtaMins] = useState(0);
  const [starting, setStarting] = useState(false);
  const [etaMinutes, setEtaMinutes] = useState(null); // committed estimate
  const [jobStartTs, setJobStartTs] = useState(null); // ms epoch when started
  const [nowTs, setNowTs] = useState(() => Date.now());

  const etaStorageKey = job_pid ? `job_eta_${job_pid}` : null;

  useEffect(() => {
    if (job_pid) getJobDetail(job_pid);
  }, [job_pid, getJobDetail]);

  // Used to block starting this job while another assigned job is already
  // in progress — only one job can be actively worked on at a time.
  useEffect(() => {
    const loadAssignedJobs = async () => {
      const { data } = await getJobsList({
        status: "assigned",
        page: 1,
        per_page: 100,
      });
      setAssignedJobsList(data || []);
    };
    loadAssignedJobs();
  }, [getJobsList]);

  useEffect(() => {
    if (!jobDetail) return;
    const initial = {};
    Object.values(jobDetail.checklist)
      .flat()
      .forEach((item) => {
        initial[item.id] = item.done;
      });
    setCheckedItems(initial);
    setDamageNotes(jobDetail.damage_notes || "");
    setWorkPhotos(jobDetail.work_photos || []);
    setJobStarted(!!jobDetail.is_started);

    // Restore a previously entered estimate/start-time for an in-progress job.
    if (jobDetail.is_started) {
      const saved = etaStorageKey ? storage.get(etaStorageKey) : null;
      if (saved?.etaMinutes && saved?.startTs) {
        setEtaMinutes(saved.etaMinutes);
        setJobStartTs(saved.startTs);
        // Sync "now" to the moment of restore so the first render isn't
        // computed against the stale timestamp captured at mount (which lags
        // by however long the job detail took to load).
        setNowTs(Date.now());
      } else {
        // No local estimate (e.g. started on another device or cleared cache) —
        // fall back to the server's estimate + start time from the job detail.
        const apiEstimate = Number(jobDetail.estimated_minutes) || 0;
        const startDate = parseUtc(jobDetail.started_at);
        if (apiEstimate > 0 && startDate) {
          const startTs = startDate.getTime();
          setEtaMinutes(apiEstimate);
          setJobStartTs(startTs);
          setNowTs(Date.now());
          if (etaStorageKey) {
            storage.set(etaStorageKey, { etaMinutes: apiEstimate, startTs });
          }
        }
      }
    }
  }, [jobDetail, etaStorageKey]);

  // Tick the timeline every second while the job is running.
  useEffect(() => {
    if (!jobStarted || !jobStartTs || !etaMinutes) return;
    setNowTs(Date.now());
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [jobStarted, jobStartTs, etaMinutes]);

  const checklistSections = jobDetail
    ? Object.entries(jobDetail.checklist).map(([title, items]) => ({
        title,
        items,
      }))
    : [];

  const allItems = checklistSections.flatMap((s) => s.items);
  const totalItems = allItems.length;
  const completedItems = allItems.filter(
    (item) => checkedItems[item.id],
  ).length;
  const progressPercent =
    totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  const isAllCompleted = totalItems > 0 && completedItems === totalItems;

  const propertyImages = jobDetail?.property_images || [];
  const removeWorkPhoto = (index) =>
    setWorkPhotos((prev) => prev.filter((_, i) => i !== index));

  const toggleCheck = (id) => {
    setCheckedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const previews = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setNewImages((prev) => [...prev, ...previews]);

    const base64Array = await Promise.all(files.map(fileToBase64));
    await uploadWorkPhoto({ job_pid: jobDetail.job_pid, photos: base64Array });
  };

  const removeNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, i) => i !== index));
  };

  const scroll = (direction) => {
    const container = scrollRef.current;
    const itemWidth = 292;
    container.scrollBy({
      left: direction === "next" ? itemWidth : -itemWidth,
      behavior: "smooth",
    });
  };

  // Opens the ETA popup. Prefills with the current estimate when editing a
  // running job; otherwise starts blank so no stale/default value carries over.
  const openEtaModal = () => {
    if (jobStarted && jobStartTs && etaMinutes) {
      setEtaHours(Math.floor(etaMinutes / 60));
      setEtaMins(etaMinutes % 60);
    } else {
      // Prefill a fresh start with the server's suggested estimate, if any.
      const apiEstimate = Number(jobDetail?.estimated_minutes) || 0;
      setEtaHours(Math.floor(apiEstimate / 60));
      setEtaMins(apiEstimate % 60);
    }
    setShowEtaModal(true);
  };

  const handleStartEnd = async () => {
    if (jobDetail && !jobDetail.is_primary) return;
    if (!jobStarted) {
      // Ask for the estimated completion time before starting.
      openEtaModal();
      return;
    }

    if (!isAllCompleted) return;

    const checklist = allItems.map((item) => ({
      id: item.id,
      done: !!checkedItems[item.id],
    }));

    const saved = await saveDamageNotes({
      job_pid: jobDetail.job_pid,
      notes: damageNotes,
      checklist,
    });

    if (saved) {
      if (etaStorageKey) storage.remove(etaStorageKey);
      navigate("/end-job", {
        state: {
          job_pid: jobDetail.job_pid,
          total_price: jobDetail.total_price,
        },
      });
    }
  };

  const adjustEta = (unit, delta) => {
    if (unit === "h") {
      setEtaHours((h) => Math.max(0, Math.min(24, h + delta)));
    } else {
      setEtaMins((m) => {
        const v = m + delta;
        if (v < 0) return 0;
        if (v > 55) return 55;
        return v;
      });
    }
  };

  // Manual typing — keep only digits and clamp within range.
  const handleEtaInput = (unit, value) => {
    const digits = value.replace(/\D/g, "");
    if (digits === "") {
      unit === "h" ? setEtaHours(0) : setEtaMins(0);
      return;
    }
    const num = parseInt(digits, 10);
    if (unit === "h") {
      setEtaHours(Math.min(24, num));
    } else {
      setEtaMins(Math.min(59, num));
    }
  };

  const etaPresets = [
    { label: "30 min", h: 0, m: 30 },
    { label: "1 hr", h: 1, m: 0 },
    { label: "1 hr 30 min", h: 1, m: 30 },
    { label: "2 hr", h: 2, m: 0 },
    { label: "3 hr", h: 3, m: 0 },
  ];

  const draftEtaMinutes = etaHours * 60 + etaMins;

  const isEditingEta = jobStarted && !!jobStartTs;

  const confirmEta = async () => {
    if (draftEtaMinutes <= 0) {
      toast.error("Please set an estimated completion time");
      return;
    }

    // Editing the estimate of a running job — keep the real start time so the
    // elapsed clock is untouched; only the target (and thus remaining) updates.
    if (isEditingEta) {
      setEtaMinutes(draftEtaMinutes);
      setNowTs(Date.now());
      setShowEtaModal(false);
      if (etaStorageKey) {
        storage.set(etaStorageKey, {
          etaMinutes: draftEtaMinutes,
          startTs: jobStartTs,
        });
      }
      return;
    }

    setStarting(true);
    const started = await startJob(jobDetail.job_pid, draftEtaMinutes);
    setStarting(false);
    if (!started) return;

    const startTs = Date.now();
    setEtaMinutes(draftEtaMinutes);
    setJobStartTs(startTs);
    setNowTs(startTs);
    setJobStarted(true);
    setShowEtaModal(false);
    if (etaStorageKey) {
      storage.set(etaStorageKey, { etaMinutes: draftEtaMinutes, startTs });
    }
  };

  const client = jobDetail?.client;
  const isCompleted = jobDetail?.is_completed === 1;
  const isHelper = jobDetail ? !jobDetail.is_primary : false;
  const minutesSpent = jobDetail?.minutes_spent ?? null;

  const todayLocalDate = (() => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  })();
  const scheduleLocalDate = getLocalDateFromUtc(jobDetail?.schedule_date_time);
  const isScheduledToday =
    !!scheduleLocalDate && scheduleLocalDate === todayLocalDate;
  const isFutureSchedule =
    !!scheduleLocalDate && scheduleLocalDate > todayLocalDate;
  const scheduledDateLabel = jobDetail?.schedule_date_time
    ? formatUtcToLocalDate(jobDetail.schedule_date_time)
    : "";
  const isOverdueSchedule =
    !!scheduleLocalDate && scheduleLocalDate < todayLocalDate && !isCompleted;

  const anotherJobInProgress = assignedJobsList.some(
    (j) => j.job_pid !== job_pid && j.status === "In Progress",
  );

  const isStartEndDisabled =
    isHelper ||
    (!jobStarted && !isScheduledToday) ||
    (!jobStarted && anotherJobInProgress) ||
    (jobStarted && !isAllCompleted);
  const disabledBtnStyle = {
    backgroundColor: "#adb5bd",
    borderColor: "#adb5bd",
    color: "#ffffff",
    opacity: 0.75,
    cursor: "not-allowed",
    boxShadow: "none",
  };

  const formatMinutes = (mins) => {
    if (!mins) return "0 min";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h} hr ${m} min` : `${h} hr`;
  };

  const formatClock = (ms) => {
    const totalSec = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
  };

  // ── Live timeline derived values ──
  const showTimeline =
    jobStarted && !isCompleted && !!etaMinutes && !!jobStartTs;
  const etaTotalMs = (etaMinutes || 0) * 60000;
  const elapsedMs = showTimeline ? Math.max(0, nowTs - jobStartTs) : 0;
  const remainingMs = etaTotalMs - elapsedMs;
  const isOvertime = remainingMs < 0;
  const rawPct = etaTotalMs > 0 ? (elapsedMs / etaTotalMs) * 100 : 0;
  const fillPct = Math.min(rawPct, 100);
  // 0–50% green, 50–75% yellow, 75%+ red
  const etaZone = rawPct < 50 ? "green" : rawPct < 75 ? "yellow" : "red";

  return (
    <div className="content-wrapper-main2">
      <div className="content-main">
        <div className="content-card mb-4 mt-0">
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              {isCompleted ? (
                <span className="badge bg-success px-3 py-2 rounded-pill fs-13">
                  <i className="fa-solid fa-circle-check me-1" />
                  Completed
                </span>
              ) : (
                !isHelper && (
                  <button
                    className="btn px-3 btn-secondary text-nowrap flex-shrink-0"
                    onClick={handleStartEnd}
                    disabled={isStartEndDisabled}
                    style={isStartEndDisabled ? disabledBtnStyle : undefined}
                  >
                    {!jobStarted ? "Start Job" : "End Job"}
                  </button>
                )
              )}
              {isHelper && (
                <span className="badge bg-primary-light text-primary px-3 py-2 rounded-pill fs-13">
                  <i className="fa-solid fa-user-group me-1" />
                  Rookie
                </span>
              )}
              {isOverdueSchedule && (
                <span className="badge bg-danger px-3 py-2 rounded-pill fs-13">
                  <i className="fa-solid fa-triangle-exclamation me-1" />
                  Overdue
                </span>
              )}
            </div>
            <div className="text-end">
              {jobDetail?.job_id && (
                <div className="mb-2">
                  <span className="badge bg-primary-light text-primary px-3 py-1 rounded-pill fs-11">
                    #{jobDetail.job_pid}
                  </span>
                </div>
              )}
              <p className="mb-0 fs-12 text-muted">Scheduled</p>
              <h5 className="mb-0 text-primary fw-bold fs-17">
                {formatUtcToLocalDateTime(jobDetail?.schedule_date_time)}{" "}
              </h5>

              {isCompleted && minutesSpent !== null && (
                <>
                  <p className="mb-0 fs-12 text-muted mt-1">Time Spent</p>
                  <h6 className="mb-0 text-success fw-bold">
                    {formatMinutes(minutesSpent)}
                  </h6>
                </>
              )}
            </div>
          </div>
          <div className="progress-track mt-3" style={{ height: "8px" }}>
            <div
              className="progress-fill"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
          <div className="d-flex justify-content-between mt-2">
            <span className="time-text fw-medium">{completedItems} done</span>
            <span className="time-text fw-medium">{totalItems} total</span>
          </div>

          {showTimeline && (
            <div className={`job-eta-timeline mt-3 ${etaZone}`}>
              <div className="jet-head">
                <div className="jet-head-block">
                  <span className="jet-label">
                    <Clock size={12} className="me-1" />
                    {isOvertime ? "Overtime" : "Time Remaining"}
                  </span>
                  <span className={`jet-time ${etaZone}`}>
                    {isOvertime ? "+" : ""}
                    {formatClock(Math.abs(remainingMs))}
                  </span>
                </div>
                <div className="jet-head-block text-end">
                  <span className="jet-label">Estimated</span>
                  <span className="jet-eta-edit">
                    {formatMinutes(etaMinutes)}
                  </span>
                </div>
              </div>

              <div className="jet-track">
                <span className="jet-zone-tick" style={{ left: "50%" }} />
                <span className="jet-zone-tick" style={{ left: "75%" }} />
                <div
                  className={`jet-fill ${etaZone}`}
                  style={{ width: `${fillPct}%` }}
                />
                <span
                  className={`jet-knob ${etaZone}`}
                  style={{ left: `${fillPct}%` }}
                />
              </div>

              <div className="jet-foot">
                <span className="jet-elapsed">
                  Elapsed {formatClock(elapsedMs)}
                </span>
                <span className={`jet-pct ${etaZone}`}>
                  {Math.round(rawPct)}%
                </span>
              </div>
            </div>
          )}

          {jobDetail?.total_price != null && (
            <div className="job-amount-line mt-3">
              <span className="job-amount-line-label">
                <i className="fa-solid fa-sack-dollar" />
                Amount
              </span>
              <span className="job-amount-line-value">
                ${Number(jobDetail.total_price).toLocaleString()}
              </span>
            </div>
          )}
          {!isCompleted && !isHelper && !jobStarted && anotherJobInProgress && (
            <div className="alert alert-warning py-2 px-3 mb-0 mt-3 fs-13 d-flex align-items-center gap-2">
              <i className="fa-solid fa-circle-exclamation" />
              <span>
                You already have another job <strong>in progress</strong>.
                Finish or end it before starting this one.
              </span>
            </div>
          )}
          {!isCompleted && isHelper && (
            <div className="alert alert-warning py-2 px-3 mb-0 mt-3 fs-13 d-flex align-items-center gap-2">
              <i className="fa-solid fa-user-group" />
              <span>
                You are assigned as a <strong>rookie</strong> on this job. Only
                the pro can start or end it.
              </span>
            </div>
          )}
          {!isCompleted && !isHelper && !jobStarted && isFutureSchedule && (
            <div className="alert alert-info py-2 px-3 mb-0 mt-3 fs-13 d-flex align-items-center gap-2">
              <i className="fa-solid fa-circle-info" />
              <span>
                This job is scheduled for <strong>{scheduledDateLabel}</strong>.
                You can start it on the scheduled date.
              </span>
            </div>
          )}
          {!isCompleted && !jobStarted && isOverdueSchedule && (
            <div className="alert alert-danger py-2 px-3 mb-0 mt-3 fs-13 d-flex align-items-center gap-2">
              <i className="fa-solid fa-triangle-exclamation" />
              <span>
                This job was scheduled for <strong>{scheduledDateLabel}</strong>{" "}
                and was not performed on time.
              </span>
            </div>
          )}
        </div>

        <div className="content-card mb-4">
          <h6 className="mb-3 fw-bold border-bottom pb-2">Client Details</h6>
          <div className="row align-items-center">
            <div className="col-md-6 mb-2">
              <div className="d-flex align-items-center gap-2">
                <i className="fa-solid fa-user fa-sm text-primary" />
                <p className="mb-0 fw-bold fs-15">{client?.name || "—"}</p>
              </div>
            </div>

            {client?.phone && (
              <div className="col-md-6 mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa-solid fa-phone fa-sm" />

                    <a
                      href={`tel:${client.phone}`}
                      className="text-decoration-none text-dark"
                    >
                      <p className="mb-0 fw-medium fs-15">{client.phone}</p>
                    </a>
                  </div>

                  <a href={`tel:${client.phone}`}>
                    <button className="btn btn-sm btn-outline-primary px-3 fs-15">
                      Call
                    </button>
                  </a>
                </div>
              </div>
            )}

            {client?.email?.trim() && (
              <div className="col-md-6 mb-2">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa-solid fa-envelope fa-sm text-info" />
                    <p
                      className="mb-0 fw-medium fs-15 text-dark"
                      style={{ cursor: "pointer" }}
                      onClick={() =>
                        window.open(`mailto:${client.email.trim()}`)
                      }
                    >
                      {client.email}
                    </p>
                  </div>
                  {/* <button
                    className="btn btn-sm btn-outline-info rounded-pill px-3 fs-15"
                    onClick={() => window.open(`mailto:${client.email.trim()}`)}
                  >
                    Mail
                  </button> */}
                </div>
              </div>
            )}
            {client?.address && (
              <div className="col-12">
                <div className="d-flex align-items-center justify-content-between pt-2 border-top">
                  <div className="d-flex align-items-center gap-2">
                    <i className="fa-solid fa-location-dot fa-sm text-danger" />
                    <p className="mb-0 fs-14 text-dark">{client.address}</p>
                  </div>
                  <a
                    href={`https://maps.google.com/?q=${encodeURIComponent(client.address)}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <button className="btn btn-sm btn-outline-danger px-3 flex-shrink-0">
                      Maps
                    </button>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>

        {propertyImages.length > 0 && (
          <>
            <div className="section-header-bg mb-3 rounded-3 py-2 px-3">
              <div className="d-flex align-items-center gap-2">
                <i className="fa-solid fa-camera text-secondary" />
                <h5 className="mb-0 fw-bold fs-19">Service Images</h5>
              </div>
            </div>

            <div className="image-slider-wrapper mb-4 position-relative">
              <button
                className="slider-btn left"
                onClick={() => scroll("prev")}
              >
                <i className="fa-solid fa-chevron-left" />
              </button>

              <div
                className="image-scroll-container rounded-4 shadow-sm"
                ref={scrollRef}
              >
                {propertyImages.map((url, idx) => (
                  <div key={idx} className="dashboard-content-img">
                    <img
                      src={url}
                      alt="service"
                      onError={(e) => {
                        e.currentTarget.src = images.placeHolderImg;
                      }}
                    />
                  </div>
                ))}
              </div>

              <button
                className="slider-btn right"
                onClick={() => scroll("next")}
              >
                <i className="fa-solid fa-chevron-right" />
              </button>
            </div>
          </>
        )}

        <div className="db-tabs mb-4">
          <button
            className={`db-tab ${activeTab === "workChecklist" ? "active" : ""}`}
            onClick={() => setActiveTab("workChecklist")}
          >
            <ListChecks size={14} />
            Work Checklist
          </button>
          <button
            className={`db-tab ${activeTab === "workPhotos" ? "active" : ""}`}
            onClick={() => setActiveTab("workPhotos")}
          >
            <ImageIcon size={14} />
            Work Photos
          </button>
        </div>

        {activeTab === "workChecklist" && (
          <div className="content-card p-4">
            {checklistSections.map((section, sIndex) => (
              <div key={sIndex} className="checklist-section mb-4">
                <h6 className="section-title fw-bold text-primary mb-3 text-capitalize">
                  {section.title}
                </h6>
                <div className="checklist-items-grid">
                  {section.items.map((item) => (
                    <div
                      key={item.id}
                      className={`checklist-item rounded-3 mb-2 border transition-all ${
                        checkedItems[item.id]
                          ? "bg-light border-success-subtle"
                          : "bg-white"
                      } ${!jobStarted || isCompleted ? "opacity-50" : ""}`}
                      onClick={() =>
                        jobStarted && !isCompleted && toggleCheck(item.id)
                      }
                      style={{
                        cursor: isCompleted
                          ? "not-allowed"
                          : jobStarted
                            ? "pointer"
                            : "default",
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <p
                          className={`mb-0 fs-14 ${checkedItems[item.id] ? "text-success fw-medium" : "text-dark"}`}
                        >
                          {item.item}
                        </p>
                        <input
                          className="form-check-input custom-radio rounded-2"
                          checked={!!checkedItems[item.id]}
                          type="radio"
                          readOnly
                          style={{
                            cursor: isCompleted
                              ? "not-allowed"
                              : jobStarted
                                ? "pointer"
                                : "default",
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}

            <div className="mt-4 pt-4 border-top">
              <p className="mb-2 fw-bold text-dark">
                Was any pre-existing damage found?
              </p>
              <textarea
                className="form-control rounded-3 p-3 shadow-none border-2"
                placeholder="Describe any damage observed before starting..."
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                disabled={!jobStarted || isCompleted}
                rows={3}
              />
            </div>
          </div>
        )}

        {isCompleted &&
          activeTab !== "workPhotos" &&
          jobDetail?.jcc_services?.length > 0 && (
            <>
              <div className="section-header-bg mb-3 rounded-3 py-2 px-3">
                <div className="d-flex align-items-center gap-2">
                  <i className="fa-solid fa-clipboard-list text-secondary" />
                  <h5 className="mb-0 fw-bold fs-19">
                    Property Condition Report
                  </h5>
                </div>
              </div>
              <div className="d-flex flex-column gap-3 mb-4">
                {jobDetail.jcc_services.map((item) => (
                  <div key={item.service_pid} className="ej-service-card">
                    <div className="ej-service-img">
                      <img
                        src={item.image || images.placeHolderImg}
                        alt={item.name}
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = images.placeHolderImg;
                        }}
                      />
                    </div>
                    <div className="d-flex align-items-center justify-content-between w-100">
                      <h6
                        className="fw-bold mb-0 fs-15"
                        style={{ color: "#0d1831" }}
                      >
                        {item.name}
                      </h6>
                      {item.status ? (
                        <span
                          className="ej-status-badge"
                          style={{
                            background: `${item.status.color}18`,
                            color: item.status.color,
                            border: `1px solid ${item.status.color}30`,
                          }}
                        >
                          {item.status.condition.charAt(0).toUpperCase() +
                            item.status.condition.slice(1)}
                        </span>
                      ) : (
                        <span
                          className="ej-status-badge text-muted"
                          style={{
                            background: "#f0f0f0",
                            border: "1px solid #ddd",
                          }}
                        >
                          Not Rated
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

        {activeTab === "workPhotos" && (
          <div className="content-card p-4">
            <div className="text-center py-4 border-2 border-dashed rounded-4 bg-light mb-4">
              <i className="fa-solid fa-cloud-arrow-up fs-1 text-muted mb-3 d-block" />
              <button
                className="btn btn-primary px-4"
                onClick={() =>
                  jobStarted && !isCompleted && fileInputRef.current.click()
                }
                disabled={!jobStarted || isCompleted}
                style={
                  !jobStarted || isCompleted ? disabledBtnStyle : undefined
                }
              >
                Upload Work Photos
              </button>
              <p className="mt-2 text-muted fs-12">
                Upload images showing the results of your work
              </p>
              <input
                type="file"
                multiple
                accept="image/*"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleFileChange}
                disabled={!jobStarted || isCompleted}
              />
            </div>

            <div className="row g-3">
              {workPhotos.map((url, index) => (
                <div className="col-6 col-md-4" key={`existing-${index}`}>
                  <div className="position-relative">
                    <div
                      className="dashboard-content-img rounded-4 overflow-hidden shadow-sm"
                      style={{ height: "140px", width: "100%" }}
                    >
                      <img
                        src={url}
                        alt="work"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    {!isCompleted && (
                      <span
                        className="remove-img-btn bg-danger text-white rounded-circle position-absolute top-0 end-0 m-2 shadow-sm"
                        onClick={() => removeWorkPhoto(index)}
                        style={{
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <i className="fa-solid fa-xmark fs-12" />
                      </span>
                    )}
                  </div>
                </div>
              ))}

              {newImages.map((img, index) => (
                <div className="col-6 col-md-4" key={`new-${index}`}>
                  <div className="position-relative">
                    <div
                      className="dashboard-content-img rounded-4 overflow-hidden shadow-sm"
                      style={{ height: "140px", width: "100%" }}
                    >
                      <img
                        src={img.preview}
                        alt="preview"
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    {!isCompleted && (
                      <span
                        className="remove-img-btn bg-danger text-white rounded-circle position-absolute top-0 end-0 m-2 shadow-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          removeNewImage(index);
                        }}
                        style={{
                          width: "24px",
                          height: "24px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                        }}
                      >
                        <i className="fa-solid fa-xmark fs-12" />
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {showEtaModal && (
        <div
          className="eta-modal-overlay"
          onClick={() => !starting && setShowEtaModal(false)}
        >
          <div className="eta-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="eta-modal-close"
              onClick={() => !starting && setShowEtaModal(false)}
              aria-label="Close"
            >
              <X size={18} />
            </button>

            <div className="eta-modal-head">
              <div className="eta-modal-icon">
                <Clock size={22} />
              </div>
              <div>
                <h5 className="eta-modal-title">
                  {isEditingEta
                    ? "Update Completion Time"
                    : "Estimated Completion Time"}
                </h5>
                <p className="eta-modal-sub">
                  {isEditingEta
                    ? "Adjust the estimate — elapsed time stays the same."
                    : "How long do you expect this job to take?"}
                </p>
              </div>
            </div>

            <div className="eta-steppers">
              <div className="eta-stepper">
                <button
                  type="button"
                  onClick={() => adjustEta("h", -1)}
                  disabled={etaHours <= 0}
                >
                  <Minus size={16} />
                </button>
                <div className="eta-stepper-val">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="eta-stepper-input"
                    value={etaHours}
                    onChange={(e) => handleEtaInput("h", e.target.value)}
                    onFocus={(e) => e.target.select()}
                    maxLength={2}
                  />
                  <small>hours</small>
                </div>
                <button
                  type="button"
                  onClick={() => adjustEta("h", 1)}
                  disabled={etaHours >= 24}
                >
                  <Plus size={16} />
                </button>
              </div>

              <div className="eta-stepper">
                <button
                  type="button"
                  onClick={() => adjustEta("m", -5)}
                  disabled={etaMins <= 0}
                >
                  <Minus size={16} />
                </button>
                <div className="eta-stepper-val">
                  <input
                    type="text"
                    inputMode="numeric"
                    className="eta-stepper-input"
                    value={etaMins}
                    onChange={(e) => handleEtaInput("m", e.target.value)}
                    onFocus={(e) => e.target.select()}
                    maxLength={2}
                  />
                  <small>minutes</small>
                </div>
                <button
                  type="button"
                  onClick={() => adjustEta("m", 5)}
                  disabled={etaMins >= 55}
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>

            <p className="eta-hint">
              Tap a value to type it, or use +/− and the quick options below.
            </p>

            <div className="eta-presets">
              {etaPresets.map((p) => {
                const active = etaHours === p.h && etaMins === p.m;
                return (
                  <button
                    key={p.label}
                    type="button"
                    className={`eta-preset ${active ? "active" : ""}`}
                    onClick={() => {
                      setEtaHours(p.h);
                      setEtaMins(p.m);
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>

            <div className="eta-total">
              <span>Total estimate</span>
              <strong>
                {draftEtaMinutes > 0 ? formatMinutes(draftEtaMinutes) : "—"}
              </strong>
            </div>

            <div className="eta-modal-actions">
              <button
                type="button"
                className="btn btn-light flex-fill"
                onClick={() => setShowEtaModal(false)}
                disabled={starting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-secondary flex-fill"
                onClick={confirmEta}
                disabled={starting || draftEtaMinutes <= 0}
              >
                {isEditingEta
                  ? "Update Estimate"
                  : starting
                    ? "Starting…"
                    : "Start Job"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardDetails;
